import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { DayOfWeek } from "@enums/DayOfWeek";
import { DynamicReportService, ExportType, IReportScheduleQueryOptions } from "@reports/_services/dynamicReports.service";
import { SchedulingFrequency } from "@reports/enums/SchedulingFrequency";
import { TimeFilterType } from "@reports/enums/TimeFilterType";
import { IDynamicReportSchedule } from "@reports/models/IDynamicReportSchedule";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { DateService } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { copy } from "angular";
import { ReportFiltersComponent } from "../report-filters/report-filters.component";
import "./report-schedule-editor.component.scss";

@Component({
    selector: "report-schedule-editor",
    template: require("./report-schedule-editor.component.html")
})
export class ReportScheduleEditorComponent implements OnChanges {

    @Input("schedule") originalSchedule: IDynamicReportSchedule;
    @Input("reportId") originalReportId: number;

    @Output() save = new EventEmitter<IDynamicReportSchedule>();
    @Output() delete = new EventEmitter<IDynamicReportSchedule>();
    @Output() cancel = new EventEmitter();

    @ViewChild(ReportFiltersComponent) reportFilters: ReportFiltersComponent;


    // ---

    dirtySchedule: IDynamicReportSchedule;
    isSaving: boolean;
    isDeleting: boolean;

    // ---

    reports: IReportDefinition[];
    selectedReport: IReportDefinition;

    timeFilterType = TimeFilterType;
    schedulingFrequency = SchedulingFrequency;
    exportType = ExportType;
    dayOfWeek = DayOfWeek;

    existingScheduleNames: string[];

    startMinuteOfDay: number;
    endMinuteOfDay: number;

    scheduleEditorForm: FormGroup;

    get sendTo(): FormArray {
        return this.scheduleEditorForm?.get("sendTo") as FormArray;
    }

    get reportDefinitionId(): number {
        return this.scheduleEditorForm?.get("reportDefinitionId").value;
    }

    get reportScheduleName(): string {
        return this.scheduleEditorForm?.get("scheduleName").value;
    }

    get repeatDate(): number {
        return this.scheduleEditorForm?.get("repeatDate").value;
    }

    get retryCountExceeded(): boolean {
        return this.dirtySchedule.RetryCount >= 50;
    }

    constructor(
        private readonly dateService: DateService,
        private readonly dynamicReportService: DynamicReportService,
        private readonly formBuilder: FormBuilder,
        private readonly translationService: TranslationService) {

        this.filterSchedulingFrequencies();
    }

    ngOnInit(): void {
        this.prepareScheduleModel(this.originalSchedule);
    }

    ngOnChanges(): void {
        this.loadReports();
        this.prepareScheduleModel(this.originalSchedule);
    }

    cancelSchedule(): void {
        this.cancel.emit();
    }

    saveSchedule(): void {
        this.isSaving = true;

        this.cleanFormData();

        this.dynamicReportService.mapPreferences(this.dirtySchedule, this.dirtySchedule.reportDefinition, "to-schedule");

        this.dynamicReportService.saveSchedule(copy(this.dirtySchedule)).then((savedSchedule: IDynamicReportSchedule) => {
            this.save.emit(savedSchedule);
        }).finally(() => {
            this.isSaving = false;
        });
    }

    reactivateSchedule(): void {
        this.dirtySchedule.RetryCount = 0;
        this.saveSchedule();
    }

    deleteSchedule(confirm: boolean): void {
        if (!confirm) {
            return;
        }

        this.dirtySchedule.Active = false;

        this.isDeleting = true;

        const objectToDelete = copy(this.dirtySchedule);

        this.dynamicReportService.saveSchedule(this.dirtySchedule).then(() => {
            this.delete.emit(objectToDelete);
        }).finally(() => {
            this.isDeleting = false;
        });
    }

    addEmailRecipient(value: string = "") {
        this.sendTo.push(this.createEmailRecipientControl(value));
    }

    removeEmailRecipient(index: number): void {
        this.sendTo.removeAt(index);
        this.validateRecipients();
    }

    onRecipientValueChanged(): void {
        this.validateRecipients();
    }

    onReportFiltersChanged(report: IReportDefinition): void {
        this.updateScheduleUsingReport(this.dirtySchedule, report);
    }

    onTimeFilterStartChange(time: string): void {
        if (!time) {
            return;
        }
        const { hour, minute } = this.getHoursAndMinutesFromTimeString(time);
        this.startMinuteOfDay = (hour * 60) + minute;
    }

    onTimeFilterEndChange(time: string): void {
        if (!time) {
            return;
        }
        const { hour, minute } = this.getHoursAndMinutesFromTimeString(time);
        this.endMinuteOfDay = (hour * 60) + minute;
    }

    reportSelected(report: IReportDefinition): void {
        this.setSelectedReport(report.Id);
    }

    clearSelectedReport(): void {
        this.selectedReport = null;
        this.dirtySchedule.reportDefinition = null;
    }

    setSelectedReport(reportId: number): void {
        if (!reportId || !this.reports?.length) {
            return;
        }

        const report = this.reports.find(report => report.Id === reportId);

        if (!report) {
            return;
        }

        this.scheduleEditorForm.get("reportDefinitionId").setValue(report.Id);

        const reportDefinition = copy(report);

        if (this.dirtySchedule.reportDefinition) {
            this.updateReportUsingSchedule(this.dirtySchedule, reportDefinition);
        } else {
            this.updateScheduleUsingReport(this.dirtySchedule, reportDefinition);
        }

        this.selectedReport = reportDefinition;
        this.dirtySchedule.reportDefinition = reportDefinition;
    }

    getReportSummaryText(): string {
        if (this.retryCountExceeded) {
            return this.translationService.t("ReportSchedules.ReportScheduleErrorMessage");
        }

        return `${this.getFrequencyText()} ${this.getPeriodText()} ${this.getRangeText()} ${this.getRecipientsString()}`;
    }

    resetDays(): void {
        this.dirtySchedule.RepeatOn = [1];
    }

    validateSelectedDays(): boolean {
        if (this.dirtySchedule.Frequency === SchedulingFrequency.Monthly && this.repeatDate > 0 && this.repeatDate < 31) {
            return true;
        }
        if (!this.dirtySchedule.RepeatOn?.length || (this.dirtySchedule.Frequency === SchedulingFrequency.Weekly && this.dirtySchedule.RepeatOn.length < 2)) {
            return false;
        }
        return true;
    }

    isFormValid(): boolean {
        return this.scheduleEditorForm?.valid
            && this.validateSelectedDays()
            && !!this.selectedReport
            && this.reportSingleResourceIsSelected();
    }


    reportFilter = (report: IReportDefinition, text: string): boolean => {
        return report.Title.toLowerCase().indexOf(text.toLowerCase()) >= 0;
    }

    // ---

    private prepareScheduleModel(schedule: IDynamicReportSchedule): void {
        if (this.dirtySchedule) {
            return;
        }

        if (schedule?.Id > 0) {
            this.dirtySchedule = copy(schedule);

            this.dynamicReportService.parseScheduleParametersJson(this.dirtySchedule);
            this.dynamicReportService.parseSchedulePreferencesJson(this.dirtySchedule);
            this.dynamicReportService.formatParametersToLocalTimezone(this.dirtySchedule.Parameters);

            this.dirtySchedule.reportDefinition.Parameters = this.dirtySchedule.Parameters;

        } else {
            this.dirtySchedule = this.dynamicReportService.createReportScheduleTemplate(this.originalReportId);
        }

        this.dynamicReportService.mapPreferences(this.dirtySchedule, this.dirtySchedule.reportDefinition, "to-report");

        this.setupForm(this.dirtySchedule);
        this.setSelectedReport(this.reportDefinitionId);

    }

    private reportSingleResourceIsSelected(): boolean {
        if (!this.reportFilters?.requireSingleResource) {
            return true;
        }

        return !!this.dirtySchedule.reportDefinition.Parameters.VehicleId || !!this.dirtySchedule.reportDefinition.Parameters.DriverId;
    }

    private loadReports() {
        this.dynamicReportService.getReports().then(reports => {
            this.reports = reports;

            if (this.originalSchedule?.Id) {
                this.setSelectedReport(this.originalSchedule.ReportDefinitionId);
            } else if (this.originalReportId) {
                this.setSelectedReport(this.originalReportId);
            }
        });

        this.dynamicReportService.getReportSchedules().then(schedules => {
            // If we are editing an existing schedule then ignore our own name
            if (this.originalSchedule?.Id) {
                schedules = schedules.filter(s => s.Id != this.originalSchedule.Id);
            }

            this.existingScheduleNames = schedules.map(schedule => schedule.Name);
        });
    }

    private setupForm(schedule: IDynamicReportSchedule) {
        this.scheduleEditorForm = this.formBuilder.group({
            scheduleName: [schedule.Name, [Validators.required, Validators.minLength(5), Validators.maxLength(100), CustomFormValidators.emptyWhiteSpace, this.uniqueScheduleNameValidator()]],
            repeatDate: [schedule.RunDate || 1, [Validators.required, Validators.min(1), Validators.max(31)]],
            reportDefinitionId: [schedule.ReportDefinitionId, [Validators.required, Validators.min(1)]],
            sendTo: new FormArray([
                ...this.createDefaultRecipients(schedule)
            ], [CustomFormValidators.minimumArrayLength(1)])
        });

        this.scheduleEditorForm.statusChanges.subscribe((status: string) => {
            if (status === "INVALID") {
                return;
            }

            this.processFormChanges();
        });
    }

    private processFormChanges(): void {
        this.mapFormToModel(this.dirtySchedule);
    }

    private createDefaultRecipients(schedule: IDynamicReportSchedule): AbstractControl[] {
        const emailControls: AbstractControl[] = [];

        schedule.SendTo?.forEach(email => emailControls.push(this.createEmailRecipientControl(email)));

        if (!emailControls.length) {
            emailControls.push(this.createEmailRecipientControl());
        }

        return emailControls;
    }

    private createEmailRecipientControl(value: string = ""): AbstractControl {
        return new FormControl(value, [Validators.required, Validators.email, CustomFormValidators.duplicateArrayItem]);
    }

    private mapFormToModel(schedule: IDynamicReportSchedule): void {
        schedule.Name = this.scheduleEditorForm.get("scheduleName").value;
        schedule.RunDate = this.scheduleEditorForm.get("repeatDate").value;
        schedule.SendTo = this.scheduleEditorForm.get("sendTo").value;
        schedule.ReportDefinitionId = this.scheduleEditorForm.get("reportDefinitionId").value;
    }

    private filterSchedulingFrequencies(): void {
        const enumsToRemove = [SchedulingFrequency.CurrentMonth, SchedulingFrequency.Overnight, SchedulingFrequency.Weekend];

        enumsToRemove.forEach(x => {
            let currentFrequency = this.schedulingFrequency as any;

            delete currentFrequency[SchedulingFrequency[x]];
            delete currentFrequency[x];
        });
    }

    private uniqueScheduleNameValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.existingScheduleNames?.length || !control.value) {
                return null;
            }

            const lowerCaseScheduleName = control.value.toLowerCase();
            const isDuplicate = this.existingScheduleNames.find(name => name.toLowerCase() === lowerCaseScheduleName);

            return isDuplicate ? { customMessage: "Please enter a unique schedule name" } : null;
        };
    }

    private validateRecipients(): void {
        setTimeout(() => {
            this.sendTo.controls.forEach(control => control.updateValueAndValidity());
        });
    }

    private cleanFormData(): void {
        this.trimScheduleName();
    }

    private trimScheduleName(): void {
        const name = this.dirtySchedule.Name.trim();
        if (!name || name.indexOf('  ') < 0) {
            return;
        }

        this.dirtySchedule.Name = name.replace('  ', ' ');
        this.trimScheduleName(); // Repeat until no unnecessary whitespace remains
    }

    private updateReportUsingSchedule(schedule: IDynamicReportSchedule, report: IReportDefinition): void {
        const scheduleModel = copy(schedule);

        this.dynamicReportService.copyScheduleParametersToReport(scheduleModel, report);
        this.dynamicReportService.copySchedulePreferencesToReport(scheduleModel, report);
    }

    private updateScheduleUsingReport(schedule: IDynamicReportSchedule, report: IReportDefinition): void {
        const reportModel = copy(report);

        reportModel.Parameters.TimeFilterStart = schedule.Parameters.TimeFilterStart
        reportModel.Parameters.TimeFilterEnd = schedule.Parameters.TimeFilterEnd
        reportModel.Parameters.TimeFilterType = schedule.Parameters.TimeFilterType

        this.dynamicReportService.copyReportParametersToSchedule(reportModel, schedule);
        this.dynamicReportService.copyReportPreferencesToSchedule(reportModel, schedule);
    }

    private getFrequencyText(): string {

        const reportName = this.reportScheduleName || this.translationService.t("ReportSchedules.ScheduleName");
        const translatedFrequency = this.translationService.tEnum(SchedulingFrequency, "SchedulingFrequency", this.dirtySchedule.Frequency);

        return this.translationService.t("ReportSchedules.WillRun", [reportName, translatedFrequency])
    }

    private getPeriodText(): string {

        switch (this.dirtySchedule.Frequency) {
            case SchedulingFrequency.Daily:
                if (this.dirtySchedule.RepeatOn.length < 1) {
                    return '';
                }
                const days = this.dirtySchedule.RepeatOn.map(day => DayOfWeek[day]);
                return this.translationService.t("ReportSchedules.ForDays", [days.join(', ')]);

            case SchedulingFrequency.Weekly:
                if (this.dirtySchedule.RepeatOn.length < 1) {
                    return '';
                }

                const startDay = this.translationService.tEnum(DayOfWeek, "DayOfWeek", this.dirtySchedule.RepeatOn[0]);
                const startDayTime = (this.getStartTime(TimeFilterType.Range) ? this.getStartTime(TimeFilterType.Range) : '') + ' ' + startDay;

                let endDay: string = '--';
                if (this.dirtySchedule.RepeatOn.length > 1) {
                    endDay = this.translationService.tEnum(DayOfWeek, "DayOfWeek", this.dirtySchedule.RepeatOn.slice(-1)[0]);
                }

                const endDayTime = (this.getEndTime(TimeFilterType.Range) ? this.getEndTime(TimeFilterType.Range) : '') + ' ' + endDay;

                return this.translationService.t("ReportSchedules.ForPeriod", [startDayTime, endDayTime]);

            case SchedulingFrequency.Monthly:
                const dayOfMonth = this.dateService.getOrdinalSuffix(this.repeatDate);
                return this.translationService.t("ReportSchedules.DayOfMonth", dayOfMonth);

            default:
                return `[${this.translationService.t("ReportSchedules.Unspecified")}]`;

        }

    }

    private getRangeText(): string {
        if (this.dirtySchedule.Parameters.TimeFilterType !== TimeFilterType.Daily) {
            return '';
        }

        const startTime = this.getStartTime(TimeFilterType.Daily);
        const endTime = this.getEndTime(TimeFilterType.Daily);

        return this.translationService.t("ReportSchedules.RangeOf", [startTime, endTime]);
    }

    private getRecipientsString(): string {
        let recipientsString = "";

        if (this.sendTo.value[0].length < 1) {
            return recipientsString;
        }

        recipientsString += this.translationService.t("ReportScheduleEditor.SendingTo", this.sendTo.value[0]);

        if (this.sendTo.value.length > 1) {
            recipientsString += " " + this.translationService.t("ReportScheduleEditor.AndXOther", this.sendTo.value.length - 1);
        }

        return recipientsString;
    }

    private getStartTime(timeFilterType: TimeFilterType): string {
        if (this.dirtySchedule.Parameters.TimeFilterType !== timeFilterType) {
            return;
        }
        return this.dirtySchedule.Parameters.TimeFilterStart;
    }

    private getEndTime(timeFilterType: TimeFilterType): string {
        if (this.dirtySchedule.Parameters.TimeFilterType !== timeFilterType) {
            return;
        }
        return this.dirtySchedule.Parameters.TimeFilterEnd;
    }

    private getHoursAndMinutesFromTimeString(time: string): { hour: number, minute: number } {
        const splitTime = time.split(':');
        const hour = Number(splitTime[0]);
        const minute = Number(splitTime[1]);

        return { hour, minute };
    }
}
